<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->

<script lang="ts">
import type vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import type { ScreenshotData } from '@src/components/ScreenshotDialog.vue';

import type { LPSAxisDir } from '../types/lps';
import type { vtkIJKSliceRepresentationProxy } from '../vtk/IJKSliceRepresentationProxy';
import type vtkLabelMapSliceRepProxy from '../vtk/LabelMapSliceRepProxy';
import type vtkLPSView2DProxy from '../vtk/LPSView2DProxy';
import type {
  BiomarkerType,
  MaskTypeBehaviour,
} from '@/src/store/analysis/data-typing';

import type { ClipboardImage } from '@/src/store/image-clipboard-store';

import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import CornerMenu from '@src/components/pixel-overlay/CornerMenu.vue';
import ScreenshotDialog from '@src/components/ScreenshotDialog.vue';
import { checkComponentViewIDPermission } from '@src/config';
import { onKeyStroke } from '@vueuse/core';

import { vec3 } from 'gl-matrix';

import {
  BufferTarget,
  CanvasSource,
  Mp4OutputFormat,
  Output,
  QUALITY_HIGH,
} from 'mediabunny';
import {
  computed,
  defineComponent,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  toRefs,
  watch,
  watchEffect,
} from 'vue';

import AppLogoComponent from '@/src/components/AppLogoComponent.vue';
import GlassDropdown from '@/src/components/GlassDropdown.vue';
import { useAnalysisStore } from '@/src/store/analysis';
import { ViewportToolMode } from '@/src/store/contours/types/tool-modes-type';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { useImageClipboardStore } from '@/src/store/image-clipboard-store';

import { manageVTKSubscription } from '../composables/manageVTKSubscription';

import { useCameraOrientation } from '../composables/useCameraOrientation';
import { useOrientationLabels } from '../composables/useOrientationLabels';
import { usePersistCameraConfig } from '../composables/usePersistCameraConfig';
import { useResizeObserver } from '../composables/useResizeObserver';
import { useResizeToFit } from '../composables/useResizeToFit';
import { useSceneBuilder } from '../composables/useSceneBuilder';
import { useViewProxy } from '../composables/useViewProxy';
import { useWidgetManager } from '../composables/useWidgetManager';
import { ToolContainer, VTKTwoViewWidgetManager } from '../constants';
import { ViewProxyType } from '../core/proxies';
import { calculateInsertionPointAngle } from '../store/contours/contour-processing/calculateInsertionPointAngl';
import {
  useCurrentStudyStore,
  validImageIndices,
} from '../store/dicom-web/current-study-store';
import { useAppState } from '../store/ocg-app-state';
import { useViewCameraStore } from '../store/view-configs/camera';
import useViewSliceStore, {
  defaultSliceConfig,
} from '../store/view-configs/slicing';
import useWindowingStore from '../store/view-configs/windowing';
import { getImageArrayTopDir, getLPSAxisFromDir } from '../utils/lps';
import useEventBus from './pixel-overlay/eventBus';
import UIDAdjustContours from './pixel-overlay/UIDAdjustContours.vue';
import UIDDisplayContours from './pixel-overlay/UIDDisplayContours.vue';
import UIDDisplayGrid from './pixel-overlay/UIDDisplayGrid.vue';
import UIDDisplayMasks from './pixel-overlay/UIDDisplayMasks.vue';
import PanTool from './tools/PanTool.vue';
import RotateTool from './tools/RotateTool.vue';
import WindowLevelTool from './tools/WindowLevelTool.vue';
import ZoomTool from './tools/ZoomTool.vue';
import ViewOverlayGrid from './ViewOverlayGrid.vue';
import { addLocalizerActors, removeActors } from './vtkTwoView-utils-localizer';

export default defineComponent({
  name: 'VtkTwoView',
  components: {
    ViewOverlayGrid,
    WindowLevelTool,
    PanTool,
    ZoomTool,
    RotateTool,
    // For overlaying pixelmaps
    UIDAdjustContours,
    UIDDisplayContours,
    UIDDisplayGrid,
    AppLogoComponent,
    UIDDisplayMasks,
    CornerMenu,
    ScreenshotDialog,
    GlassDropdown,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    disableContours: {
      type: Boolean,
      required: false,
      default: false,
    },
    rotateToRefPoint: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const windowingStore = useWindowingStore();
    const viewSliceStore = useViewSliceStore();
    const viewCameraStore = useViewCameraStore();
    const currentStudyStore = useCurrentStudyStore();
    const viewportStateStore = useViewportStateStore();
    const imageClipboardStore = useImageClipboardStore();

    const appStateStore = useAppState();

    const { id: viewID, title: viewTitle, rotateToRefPoint } = toRefs(props);

    const vtkContainerRef = ref<HTMLElement>();
    const contourOverlayRef = ref<any>();
    const maskOverlayRef = ref<any>();
    const annotationOverlayRef = ref<any>();
    const formattedContourValues = ref('');
    const contoursToggledOff = ref(false);
    const masksToggledOff = ref(false);

    const analysisStore = useAnalysisStore();
    const biomarkerContainer = computed(() =>
      analysisStore.getBiomarkerContainer(),
    );

    const selectedMaskType = computed(() =>
      viewportStateStore.getSelectedMaskType(viewID.value),
    );

    const selectedBiomarker = computed(() => {
      if (!selectedMaskType.value) return null;
      return viewportStateStore.getSelectedMask(
        viewID.value,
        selectedMaskType.value,
      );
    });

    const availableBiomarkers = computed(() => {
      if (!selectedMaskType.value) return [];
      return (
        biomarkerContainer.value.getMaskTypesBasedOnBiomarkerType(
          selectedMaskType.value as MaskTypeBehaviour,
        ) || []
      );
    });

    const getLabelFromBiomarkerType = (biomarker: BiomarkerType | null) => {
      if (!biomarker) return 'Select Biomarker';
      return (
        biomarkerContainer.value?.getLabelFromBiomarkerType(biomarker)
        || biomarker
      );
    };

    const availableBiomarkerOptions = computed(() => {
      return availableBiomarkers.value.map(b => ({
        value: b,
        label: getLabelFromBiomarkerType(b),
      }));
    });

    // --- computed vars --- //
    const currentImageIndices = computed(() =>
      viewportStateStore.getSelectedImageIndices(viewID.value),
    );

    const showMasks = computed(
      () =>
        viewportStateStore.getShowMasks(viewID.value)
        && checkComponentViewIDPermission('UIDDisplayMasks', viewID.value),
    );

    const showOverlayImages = computed(() =>
      viewportStateStore.getShowOverlayImages(viewID.value),
    );

    const toggleMasks = () => {
      const current = viewportStateStore.getShowMasks(viewID.value);
      masksToggledOff.value = current;
      viewportStateStore.setShowMasks(viewID.value, !current);
    };

    const clearViewport = () => {
      viewportStateStore.clearViewportState(viewID.value);
    };

    const currentImageIndicesValid = computed(() =>
      validImageIndices(currentImageIndices.value),
    );

    const currentSeriesIndexString = computed(() =>
      currentImageIndices.value.seriesIdx.toString(),
    );

    const isCineSeries = computed(() => {
      if (!currentImageIndices.value || !currentImageIndicesValid.value) {
        return false;
      }
      return (
        currentStudyStore.getImagesCount(
          currentImageIndices.value.seriesIdx,
          currentImageIndices.value.sliceIdx,
        ) > 5
      );
    });

    // ANALYSIS UTILS
    // TODO!: Move to composable
    const selectBiomarker = (
      maskType: string | null,
      biomarker: BiomarkerType,
    ) => {
      if (!maskType) return;
      viewportStateStore.setShowMasks(viewID.value, true);
      if (maskType === 'Static') {
        const { seriesIdx, sliceIdx } = currentImageIndices.value;
        const seriesUID = currentStudyStore.getSeriesUID(seriesIdx);
        const mask = biomarkerContainer.value.getStaticMaskBySliceAndType(
          seriesUID,
          sliceIdx,
          biomarker,
        );
        if (mask) {
          const imageUID = mask.getImageUID();
          const frameInfo = currentStudyStore.getIndexByImageUID(imageUID);
          if (frameInfo) {
            const frameIndex = {
              ...currentImageIndices.value,
              imageIdx: frameInfo.imageIndex,
            };
            viewportStateStore.setSelectedImageIndices(
              viewID.value,
              frameIndex,
              true,
            );
          }
        }
      }
      viewportStateStore.setSelectedMarker(viewID.value, maskType, biomarker);
    };

    const hasAnalyzedSelection = computed(() => {
      if (!currentImageIndices.value || !currentImageIndicesValid.value)
        return false;
      const { seriesIdx, sliceIdx } = currentImageIndices.value;
      return biomarkerContainer.value.isSeriesAnalyzed(seriesIdx, sliceIdx);
    });

    const selectMaskType = (maskType: 'Static' | 'Dynamic') => {
      const existingBiomarker = viewportStateStore.getSelectedMask(
        viewID.value,
        maskType,
      );
      const list
        = biomarkerContainer.value.getMaskTypesBasedOnBiomarkerType(maskType)
          || [];
      const nextBiomarker
        = existingBiomarker || (list[0] as BiomarkerType) || null;

      masksToggledOff.value = false;
      viewportStateStore.setShowMasks(viewID.value, true);

      if (nextBiomarker) {
        selectBiomarker(maskType, nextBiomarker);
      }
      else {
        viewportStateStore.initViewportState(viewID.value);
        viewportStateStore.viewportStates[viewID.value].selectedMaskType
          = maskType;
      }

      if (maskType === 'Static') {
        viewportStateStore.setShowContours(viewID.value, false);
        contoursToggledOff.value = true;
      }
    };

    const onDrop = (event: {
      preventDefault: () => void;
      dataTransfer: { getData: (arg0: string) => string } | null;
    }) => {
      event.preventDefault();

      if (event.dataTransfer) {
        const indices = JSON.parse(event.dataTransfer.getData('ImageIndices'));
        viewportStateStore.setSelectedImageIndices(viewID.value, indices);
      }
    };

    const curSeriesID = computed(() =>
      currentStudyStore.getSeriesUID(currentImageIndices.value.seriesIdx),
    );

    const curImageID = computed(() =>
      currentStudyStore.getImageUID(currentImageIndices.value),
    );

    const curImageMetadata = computed(() =>
      currentStudyStore.getVtkMetaDataIndex(currentImageIndices.value),
    );

    const curImageData = computed(() =>
      currentStudyStore.getVtkImageDataIndex(
        currentImageIndices.value,
        viewID.value,
      ),
    );

    const numImages = computed(() =>
      currentStudyStore.getImagesCount(
        currentImageIndices.value.seriesIdx,
        currentImageIndices.value.sliceIdx,
      ),
    );

    const mainImageOrientation = computed(() => {
      if (!curImageData.value) {
        return undefined;
      }

      let mainImageOrientationCalc = 'UNDEFINED';
      if (curImageMetadata.value.lpsOrientation.Axial === 2) {
        mainImageOrientationCalc = 'AXIAL';
      }
      else if (curImageMetadata.value.lpsOrientation.Sagittal === 2) {
        mainImageOrientationCalc = 'SAGITTAL';
      }
      else if (curImageMetadata.value.lpsOrientation.Coronal === 2) {
        mainImageOrientationCalc = 'CORONAL';
      }
      return mainImageOrientationCalc;

      /*
      if (!curImageData.value) {
        return undefined;
      }
      const directions = curImageData.value.getDirection();
      const normalDirection = [directions[6], directions[7], directions[8]];
      let mainImageOrientationCalc = 'UNDEFINED';
      // Calculate the absolute values of the normal direction components
      const absNormalX = Math.abs(normalDirection[0]);
      const absNormalY = Math.abs(normalDirection[1]);
      const absNormalZ = Math.abs(normalDirection[2]);

      if (absNormalZ >= absNormalX && absNormalZ >= absNormalY) {
        mainImageOrientationCalc = 'AXIAL';
      } else if (absNormalX >= absNormalY && absNormalX >= absNormalZ) {
        mainImageOrientationCalc = 'SAGITTAL';
      } else {
        mainImageOrientationCalc = 'CORONAL';
      }
      return mainImageOrientationCalc;
      */
    });

    const viewUp = computed(() => getImageArrayTopDir(curImageMetadata.value));

    const viewDirection = computed(() => {
      let viewDirectionCalc = 'Superior';
      if (mainImageOrientation.value === 'AXIAL') {
        viewDirectionCalc = 'Superior';
      }
      if (mainImageOrientation.value === 'SAGITTAL') {
        viewDirectionCalc = 'Right';
      }
      if (mainImageOrientation.value === 'CORONAL') {
        viewDirectionCalc = 'Posterior';
      }
      return viewDirectionCalc as LPSAxisDir;
    });

    const viewAxis = computed(() => getLPSAxisFromDir(viewDirection.value));

    const sliceConfigDefaults = defaultSliceConfig();
    const sliceConfig = computed(() =>
      viewSliceStore.getConfig(viewID.value, curImageID.value),
    );
    const sliceMin = computed(
      () => sliceConfig.value?.min ?? sliceConfigDefaults.min,
    );
    const sliceMax = computed(
      () => sliceConfig.value?.max ?? sliceConfigDefaults.max,
    );

    const studyDicomInfo = computed(() => currentStudyStore.getStudyInfo);
    const seriesDicomInfo = computed(() =>
      curSeriesID.value
        ? currentStudyStore.$state.seriesInfoDicom[curSeriesID.value]
        : null,
    );
    const imageDicomInfo = computed(() =>
      curImageID.value
        ? currentStudyStore.$state.imageInfoDicom[curImageID.value]
        : null,
    );

    const currentImageTypeString = computed(
      () => imageDicomInfo.value?.ImageType ?? '',
    );

    const currentWlId = computed(
      () => `${currentSeriesIndexString.value}-${currentImageTypeString.value}`,
    );

    const wlConfig = computed({
      get: () => windowingStore.getConfig(viewID.value, currentWlId.value),
      set: (newValue) => {
        if (currentWlId.value !== '' && newValue != null) {
          windowingStore.updateConfig(
            viewID.value,
            currentWlId.value,
            newValue,
          );
        }
      },
    });

    const windowWidth = computed(() => wlConfig.value?.width);
    const windowLevel = computed(() => wlConfig.value?.level);

    const dicomInfo = computed(() => {
      const defaultDicomInfo = {
        patientName: 'not available',
        patientBirthDate: 'not available',
        patientSex: 'not available',
        institutionName: '',
        studyDescription: '',
        seriesDescription: 'not available',
        seriesNumber: 0,
        imageInstanceNumber: 0,
        imageRows: 0,
        imageColumns: 0,
        imageSliceThickness: 0,
        imageProtocolName: '',
        imageTriggerTime: 0,
        imageCalculatedSliceLocation: 0,
        imageImageComments: '',
        imageCalculatedHeartRate: 0,
        imageCalculatedSliceTime: 0,
        imageVencImageDescription: '',
      };

      if (!curImageID.value) {
        return defaultDicomInfo;
      }

      if (studyDicomInfo.value) {
        defaultDicomInfo.patientName = studyDicomInfo.value.PatientName;
        defaultDicomInfo.patientBirthDate
          = studyDicomInfo.value.PatientBirthDate;
        defaultDicomInfo.patientSex = studyDicomInfo.value.PatientSex;
        defaultDicomInfo.institutionName = studyDicomInfo.value.InstitutionName;
        defaultDicomInfo.studyDescription
          = studyDicomInfo.value.StudyDescription;
      }

      if (seriesDicomInfo.value) {
        defaultDicomInfo.seriesDescription
          = seriesDicomInfo.value.SeriesDescription;
        defaultDicomInfo.seriesNumber = Number.parseInt(
          seriesDicomInfo.value.SeriesNumber,
          10,
        );
      }

      if (imageDicomInfo.value) {
        defaultDicomInfo.imageInstanceNumber
          = imageDicomInfo.value.InstanceNumber;
        defaultDicomInfo.imageRows = imageDicomInfo.value.Rows;
        defaultDicomInfo.imageColumns = imageDicomInfo.value.Columns;
        defaultDicomInfo.imageSliceThickness
          = imageDicomInfo.value.SliceThickness ?? 0;
        defaultDicomInfo.imageProtocolName
          = imageDicomInfo.value.ProtocolName ?? '';
        defaultDicomInfo.imageTriggerTime
          = imageDicomInfo.value.TriggerTime ?? 0;
        defaultDicomInfo.imageCalculatedSliceLocation
          = imageDicomInfo.value.CalculatedSliceLocation;
        defaultDicomInfo.imageImageComments
          = imageDicomInfo.value.ImageComments ?? '';
        defaultDicomInfo.imageCalculatedHeartRate
          = imageDicomInfo.value.CalculatedHeartRate ?? 0;
        defaultDicomInfo.imageCalculatedSliceTime
          = imageDicomInfo.value.CalculatedSliceTime?.[
            currentImageIndices.value.seriesIdx
          ] ?? 0;
        defaultDicomInfo.imageVencImageDescription
          = imageDicomInfo.value.VencImageDescription ?? '';
      }

      return defaultDicomInfo;
    });

    const sliceDomain = computed(() => {
      const { lpsOrientation, dimensions } = curImageMetadata.value;
      const ijkIndex = lpsOrientation[viewAxis.value];
      const dimMax = dimensions[ijkIndex];

      return {
        min: 0,
        max: dimMax - 1,
      };
    });

    const showContours = computed(() => {
      return (
        viewportStateStore.getShowContours(viewID.value)
        && checkComponentViewIDPermission('UIDDisplayContours', viewID.value)
      );
    });

    // --- view proxy setup --- //

    const { viewProxy, setContainer: setViewProxyContainer }
      = useViewProxy<vtkLPSView2DProxy>(viewID, ViewProxyType.Slice);

    onBeforeMount(() => {
      // do this before mount, as the ManipulatorTools run onMounted
      // before this component does.
      viewProxy.value.getInteractorStyle2D().removeAllManipulators();
    });

    onMounted(() => {
      setViewProxyContainer(vtkContainerRef.value);
      viewProxy.value.setOrientationAxesVisibility(false);

      vtkContainerRef.value?.addEventListener('wheel', (event) => {
        if (event.deltaY < 0) {
          const newIndices = currentStudyStore.getPreviousImageIndex(
            currentImageIndices.value,
          );
          const adjusted = viewportStateStore.getCompatibleImageIndex(
            viewID.value,
            newIndices,
            currentImageIndices.value,
          );
          viewportStateStore.setSelectedImageIndices(viewID.value, adjusted);
        }
        else if (event.deltaY > 0) {
          const newIndices = currentStudyStore.getNextImageIndex(
            currentImageIndices.value,
          );
          const adjusted = viewportStateStore.getCompatibleImageIndex(
            viewID.value,
            newIndices,
            currentImageIndices.value,
          );
          viewportStateStore.setSelectedImageIndices(viewID.value, adjusted);
        }
      });
      contoursToggledOff.value = !showContours.value;
    });

    const debugProxyView = () => {
      console.log(viewProxy.value.getRenderer().getActors());
      viewProxy.value
        .getRenderer()
        .getActors()
        .forEach((actor, index) => {
          console.log(`Actor ${index + 1}:`);
          console.log(`  Type: ${actor.getClassName()}`);
          if (actor.isA('vtkImageSlice')) {
            console.log('  This actor is a vtkImageSlice.');
          }
          else {
            console.log('  This actor is not a vtkImageSlice.');
          }
        });
    };

    const screenshotDialog = ref(false);
    const screenshotDataUrl = ref('');
    const screenshotDimensions = ref({ width: 0, height: 0 });

    const showAnnotations = computed(() =>
      viewportStateStore.getShowAnnotations(viewID.value),
    );

    async function captureScreenshot() {
      const renderWindow = viewProxy.value.getRenderWindow();

      const glCanvas = renderWindow.getViews()[0].getCanvas();
      if (!glCanvas) {
        return;
      }
      const { width, height } = glCanvas;

      const out = document.createElement('canvas');
      out.width = width;
      out.height = height;
      const ctx = out.getContext('2d');
      if (!ctx) {
        return;
      }

      await composeCurrentFrame(ctx, width, height);

      screenshotDataUrl.value = out.toDataURL('image/png');
      screenshotDimensions.value = { width, height };
      screenshotDialog.value = true;
    }

    async function composeCurrentFrame(
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
    ) {
      const renderWindow = viewProxy.value.getRenderWindow();
      const imageURL = await renderWindow.captureImages()[0];
      if (!imageURL) return;

      const baseImg = new Image();
      await new Promise<void>((resolve, reject) => {
        baseImg.onload = () => resolve();
        baseImg.onerror = reject;
        baseImg.src = imageURL;
      });
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(baseImg, 0, 0, width, height);

      // contours: pure SVG → serialize
      if (!props.disableContours && showContours.value) {
        const el = contourOverlayRef.value?.$el ?? contourOverlayRef.value;
        const svgEl = el?.querySelector?.('svg');
        const rect = el?.getBoundingClientRect?.();
        if (svgEl && rect?.width) {
          const clone = svgEl.cloneNode(true) as SVGElement;
          clone.setAttribute('width', String(rect.width));
          clone.setAttribute('height', String(rect.height));
          clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          const xml = new XMLSerializer().serializeToString(clone);
          const img = new Image();
          await new Promise<void>((res, rej) => {
            img.onload = () => res();
            img.onerror = rej;
            img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
          });
          ctx.drawImage(img, 0, 0, width, height);
        }
      }

      // masks: pure SVG → serialize
      if (showMasks.value) {
        const el = maskOverlayRef.value?.$el ?? maskOverlayRef.value;
        const svgEl = el?.querySelector?.('svg');
        const rect = el?.getBoundingClientRect?.();
        if (svgEl && rect?.width) {
          const clone = svgEl.cloneNode(true) as SVGElement;
          clone.setAttribute('width', String(rect.width));
          clone.setAttribute('height', String(rect.height));
          clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          const xml = new XMLSerializer().serializeToString(clone);
          const img = new Image();
          await new Promise<void>((res, rej) => {
            img.onload = () => res();
            img.onerror = rej;
            img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
          });
          ctx.drawImage(img, 0, 0, width, height);
        }
      }

      // annotations: paint text from live DOM metrics (html2canvas mishandles
      // this flex overlay — wrong vertical placement and inflated fonts).
      if (showAnnotations.value) {
        const el = annotationOverlayRef.value?.$el ?? annotationOverlayRef.value;
        const rect = el?.getBoundingClientRect?.();
        if (el && rect?.width && rect?.height) {
          drawAnnotationOverlayToCanvas(ctx, el, rect, width, height);
        }
      }
    }

    /**
     * Draw viewer corner/edge annotation text onto the screenshot canvas using
     * each node's on-screen box and computed font, scaled into GL pixel space.
     */
    function drawAnnotationOverlayToCanvas(
      ctx: CanvasRenderingContext2D,
      root: HTMLElement,
      rootRect: DOMRect,
      targetWidth: number,
      targetHeight: number,
    ) {
      const scaleX = targetWidth / rootRect.width;
      const scaleY = targetHeight / rootRect.height;
      // Uniform scale so fonts match on-screen proportions (no stretch).
      const scale = Math.min(scaleX, scaleY);

      const textNodes = root.querySelectorAll<HTMLElement>(
        '.text-on-viewer, .annotation-cell pre',
      );

      ctx.save();
      for (const node of textNodes) {
        const text = node.innerText?.replace(/\s+$/u, '') ?? '';
        if (!text) continue;

        // Skip nodes that are not visible
        const style = getComputedStyle(node);
        if (style.visibility === 'hidden' || style.display === 'none') continue;
        if (Number.parseFloat(style.opacity || '1') === 0) continue;

        const nodeRect = node.getBoundingClientRect();
        if (nodeRect.width <= 0 || nodeRect.height <= 0) continue;

        const fontSize = Number.parseFloat(style.fontSize) || 10;
        const fontStyle = style.fontStyle || 'normal';
        const fontWeight = style.fontWeight || '400';
        const fontFamily = style.fontFamily || 'monospace';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize * scale}px ${fontFamily}`;
        ctx.fillStyle = style.color || '#fff';
        ctx.textBaseline = 'top';

        const align = style.textAlign;
        if (align === 'center' || align === 'right') {
          ctx.textAlign = align;
        }
        else {
          ctx.textAlign = 'left';
        }

        // Optional shadow for readability on bright images
        const shadow = style.textShadow;
        if (shadow && shadow !== 'none') {
          ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
          ctx.shadowBlur = 2 * scale;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        }
        else {
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
        }

        const lines = text.split('\n');
        const lineHeight
          = (Number.parseFloat(style.lineHeight) || fontSize * 1.2) * scale;

        let anchorX = (nodeRect.left - rootRect.left) * scaleX;
        if (ctx.textAlign === 'center') {
          anchorX = (nodeRect.left - rootRect.left + nodeRect.width / 2) * scaleX;
        }
        else if (ctx.textAlign === 'right') {
          anchorX = (nodeRect.right - rootRect.left) * scaleX;
        }
        const anchorY = (nodeRect.top - rootRect.top) * scaleY;

        lines.forEach((line, i) => {
          ctx.fillText(line, anchorX, anchorY + i * lineHeight);
        });
      }
      ctx.restore();
    }

    function handleScreenshotSave(screenshotData: ScreenshotData) {
      const clipboardImage: Partial<ClipboardImage> = {
        imageDataURL: screenshotData.imageDataURL,
        title: screenshotData.title,
        description: screenshotData.description,
        tags: screenshotData.tags,
        viewportId: viewID.value,
        seriesIdx: currentImageIndices.value.seriesIdx,
        sliceIdx: currentImageIndices.value.sliceIdx,
        imageIdx: currentImageIndices.value.imageIdx,
        width: screenshotDimensions.value.width,
        height: screenshotDimensions.value.height,
        colorbarInfo: screenshotData.colorbarInfo,
      };

      imageClipboardStore.addImage(clipboardImage);
    }

    function handleScreenshotDiscard() {
      screenshotDataUrl.value = '';
    }

    const currentVtkImageAvailableForRendering = computed(() =>
      currentStudyStore.imageAvailableForRendering(curImageID.value),
    );

    // to avoid flickering when loading images
    // load image if available for rendering
    watch(
      currentVtkImageAvailableForRendering,
      () => {
        if (currentVtkImageAvailableForRendering.value) {
          viewProxy.value.getRenderWindow().getInteractor().enable();
          viewProxy.value.renderLater();
        }
        else {
          viewProxy.value.getRenderWindow().getInteractor().disable();
        }
      },
      { immediate: true },
    );

    watchEffect(() => {
      const ijkIndex = curImageMetadata.value.lpsOrientation[viewAxis.value];
      viewProxy.value.setSlicingMode('IJK'[ijkIndex]);
    });

    watch(
      [viewID, curImageID, viewDirection],
      ([viewID_, imageID, viewDir]) => {
        if (!imageID || sliceConfig.value != null) {
          return;
        }

        viewSliceStore.updateConfig(viewID_, imageID, {
          ...sliceDomain.value,
          axisDirection: viewDir,
        });
        viewSliceStore.resetSlice(viewID_, imageID);
      },
      { immediate: true },
    );

    // Cine Playing:
    const cineFramerate = ref(60); // You can change this value dynamically
    const lastUpdate = ref(Date.now());
    const animationFrameId = ref<any>(null);
    const cineRunning = ref(false);

    onBeforeUnmount(() => {
      setViewProxyContainer(null);
      // stop cine
      if (animationFrameId.value) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
        cineRunning.value = false;
      }
    });

    function playCine() {
      // If the function is already running, stop it
      if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
        cineRunning.value = false;
        return;
      }

      cineRunning.value = true;

      function step() {
        const now = Date.now();
        const delta = now - lastUpdate.value;
        if (delta > cineFramerate.value) {
          if (
            currentVtkImageAvailableForRendering.value
            && curImageData.value
          ) {
            const nextImageIndices = currentStudyStore.getNextImageIndex(
              currentImageIndices.value,
            );
            const correctedIndices = viewportStateStore.getCompatibleImageIndex(
              viewID.value,
              nextImageIndices,
              currentImageIndices.value,
            );
            if (
              correctedIndices.imageIdx === currentImageIndices.value.imageIdx
              && correctedIndices.sliceIdx === currentImageIndices.value.sliceIdx
              && correctedIndices.seriesIdx === currentImageIndices.value.seriesIdx
            ) {
              cancelAnimationFrame(animationFrameId.value);
              animationFrameId.value = null;
              cineRunning.value = false;
              return;
            }
            viewportStateStore.setSelectedImageIndices(
              viewID.value,
              correctedIndices,
            );
            lastUpdate.value = now - (delta % cineFramerate.value);
          }
        }
        animationFrameId.value = requestAnimationFrame(step);
      }
      animationFrameId.value = requestAnimationFrame(step);
    }

    function reduceFramerate() {
      cineFramerate.value = Math.min(480, cineFramerate.value * 2);
    }
    function increaseFramerate() {
      cineFramerate.value = Math.max(15, cineFramerate.value / 2);
    }

    // ToDo replace with offscreen rendering
    const movieRecording = ref(false);

    function waitForRenderReady(timeoutMs = 1000): Promise<void> {
      return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
          const ready
            = currentVtkImageAvailableForRendering.value && !!curImageData.value;
          if (ready || Date.now() - start > timeoutMs) {
            // one extra frame so D3/VTK finish painting before we snapshot
            requestAnimationFrame(() => resolve());
          }
          else {
            requestAnimationFrame(tick);
          }
        };
        requestAnimationFrame(tick);
      });
    }

    async function captureMovie(fps = 15) {
      if (movieRecording.value || !isCineSeries.value) return;
      // stop live cine if running
      if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
        cineRunning.value = false;
      }
      movieRecording.value = true;
      const startIndices = { ...currentImageIndices.value };
      try {
        const renderWindow = viewProxy.value.getRenderWindow();
        const glCanvas = renderWindow.getViews()[0].getCanvas();
        if (!glCanvas) return;
        // H.264 requires even dimensions
        const width = glCanvas.width & ~1;
        const height = glCanvas.height & ~1;
        const out = document.createElement('canvas');
        out.width = width;
        out.height = height;
        const ctx = out.getContext('2d');
        if (!ctx) return;
        const output = new Output({
          format: new Mp4OutputFormat(),
          target: new BufferTarget(),
        });
        const source = new CanvasSource(out, {
          codec: 'avc',
          bitrate: QUALITY_HIGH, // or a number like 4e6
        });
        output.addVideoTrack(source, { frameRate: fps });
        await output.start();
        const { seriesIdx, sliceIdx } = startIndices;
        const frames = currentStudyStore.getImagesCount(seriesIdx, sliceIdx);
        for (let i = 0; i < frames; i++) {
          await waitForRenderReady();
          await composeCurrentFrame(ctx, width, height);
          await source.add(i / fps, 1 / fps); // encodes current canvas pixels
          // advance one phase (same as playCine)
          const next = currentStudyStore.getNextImageIndex(currentImageIndices.value);
          const corrected = viewportStateStore.getCompatibleImageIndex(
            viewID.value,
            next,
            currentImageIndices.value,
          );
          viewportStateStore.setSelectedImageIndices(viewID.value, corrected);
        }
        await output.finalize();
        const blob = new Blob([output.target.buffer!], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${viewTitle.value}.mp4`;
        a.click();
        URL.revokeObjectURL(url);
      }
      finally {
        viewportStateStore.setSelectedImageIndices(viewID.value, startIndices);
        movieRecording.value = false;
      }
    }

    // --- keyboard --- //

    const hover = ref(false);

    const onKeyDown = (event: KeyboardEvent) => {
      // if (event.repeat) return; // Ignore repeated key presses
      if (event.repeat) return;
      if (!curImageID.value || !hover.value) return;

      switch (event.key) {
        case ' ': {
          playCine();
          break;
        }
        default:
          break;
      }
      event.stopPropagation();
      event.preventDefault();
    };
    onKeyStroke([' '], onKeyDown);

    // --- resizing --- //
    const canvasWidth = ref(0);
    const canvasHeight = ref(0);
    const updateCanvasSize = () => {
      viewProxy.value.resize();
      if (vtkContainerRef.value) {
        canvasWidth.value = vtkContainerRef.value.getBoundingClientRect().width;
        canvasHeight.value
          = vtkContainerRef.value.getBoundingClientRect().height;
      }
    };

    useResizeObserver(vtkContainerRef, () => updateCanvasSize());

    // const canvasWidth = computed(() => {
    //   if (vtkContainerRef.value) {
    //     return vtkContainerRef.value.getBoundingClientRect().width;
    //   }
    //   return 0;
    // });

    // const canvasHeight = computed(() => {
    //   if (vtkContainerRef.value) {
    //     return vtkContainerRef.value.getBoundingClientRect().height;
    //   }
    //   return 0;
    // });

    // Used by SVG tool widgets for resizeCallback
    const toolContainer = ref<HTMLElement>();
    provide(ToolContainer, toolContainer);

    // --- widget manager --- //

    const { widgetManager } = useWidgetManager(viewProxy);
    provide(VTKTwoViewWidgetManager, widgetManager);

    // --- window/level setup --- //

    function resetWindowLevelToDefault() {
      if (
        !validImageIndices(currentImageIndices.value)
        || !curImageData.value
        || !currentSeriesIndexString.value
      ) {
        return;
      }
      const range = curImageData.value.getPointData().getScalars().getRange();
      const min = range[0];
      const max = range[1];

      const width
        = useCurrentStudyStore().imageInfoDicom?.[curImageID.value]?.WindowWidth || max - min;
      const level
        = useCurrentStudyStore().imageInfoDicom?.[curImageID.value]?.WindowCenter || (min + max) / 2;

      wlConfig.value = {
        width,
        level,
        min,
        max,
      };
    }

    watch(
      curImageData,
      (imageData) => {
        if (
          !validImageIndices(currentImageIndices.value)
          || !imageData
          || !currentSeriesIndexString.value
        ) {
          return;
        }

        const hasConfig = !!windowingStore.getConfig(
          viewID.value,
          currentWlId.value,
        );

        const range = imageData.getPointData().getScalars().getRange();
        const min = range[0];
        const max = range[1];

        if (hasConfig && wlConfig.value) {
          wlConfig.value = {
            width: wlConfig.value.width,
            level: wlConfig.value.level,
            min,
            max,
          };
          return;
        }
        resetWindowLevelToDefault();
      },
      {
        immediate: true,
      },
    );

    // --- reference line setup --- //

    const crossReferenceImageUids = computed(() =>
      viewportStateStore.getReferenceImageUids(viewID.value),
    );
    const crossReferenceActors = ref<vtkActor[]>([]);

    function updateReferenceLines() {
      if (!viewProxy.value) return;

      // clear the previous lines
      removeActors(viewProxy.value, crossReferenceActors.value);
      crossReferenceActors.value = [];

      const imageInfo = curImageID.value
        ? currentStudyStore.$state.imageInfoDicom[curImageID.value]
        : null;

      if (
        imageInfo
        && curImageMetadata.value?.indexToWorld
        && curImageMetadata.value.name !== '(none)'
      ) {
        const activeReferenceImageUid
          = viewportStateStore.getActiveReferenceImageUid(viewID.value);
        const showOutline = viewportStateStore.getShowLocalizerOutline(
          viewID.value,
        );

        crossReferenceImageUids.value.forEach((refImageUid) => {
          const refInfo = currentStudyStore.$state.imageInfoDicom[refImageUid];
          if (!refInfo) return;
          const actors = addLocalizerActors(
            viewProxy.value,
            imageInfo,
            refInfo,
            curImageMetadata.value.indexToWorld,
            refImageUid === activeReferenceImageUid,
            showOutline,
          );
          if (actors.length) crossReferenceActors.value.push(...actors);
        });
      }

      viewProxy.value.renderLater();
    }

    // redraw when the reference set, the active line, the outline toggle,
    // or the current image changes
    watch(
      [
        crossReferenceImageUids,
        () => viewportStateStore.getActiveReferenceImageUid(viewID.value),
        () => viewportStateStore.getShowLocalizerOutline(viewID.value),
        curImageID,
        () => curImageMetadata.value?.name,
      ],
      updateReferenceLines,
    );

    onBeforeUnmount(() => {
      removeActors(viewProxy.value, crossReferenceActors.value);
      crossReferenceActors.value = [];
    });

    // --- scene setup --- //
    const overlayIDs = ref<string[]>([]);

    const { baseImageRep, labelmapReps, layerReps, overlayReps }
      = useSceneBuilder<
        vtkIJKSliceRepresentationProxy,
        vtkLabelMapSliceRepProxy,
        vtkIJKSliceRepresentationProxy,
        vtkIJKSliceRepresentationProxy
      >(viewID, {
        baseImage: curImageID,
        overlays: overlayIDs,
      });

    // --- overlay images setup --- //
    const currentOverlayData = computed(() => {
      return currentStudyStore.getOverlayVtkImageData(curImageID.value);
    });
    watch(
      [baseImageRep, showOverlayImages, curImageID, currentOverlayData],
      () => {
        if (
          !showOverlayImages.value
          || !curImageID.value
          || !baseImageRep.value
        ) {
          overlayIDs.value = [];
          return;
        }
        const overlayID = currentStudyStore.registerOverlayDataProxy(
          curImageID.value,
        );
        overlayIDs.value = overlayID ? [overlayID] : [];
      },
      { immediate: true },
    );

    watch(
      overlayReps,
      (reps) => {
        reps.forEach((rep: any) => {
          const actors = rep.getActors?.();
          if (actors?.length) {
            actors[0].getProperty().setIndependentComponents(false);
            actors[0].getProperty().setInterpolationType(1);
            actors[0].getProperty().setColorWindow(255);
            actors[0].getProperty().setColorLevel(127.5);
            actors[0].getProperty().setOpacity(1.0);
          }
        });
        viewProxy.value.renderLater();
      },
      { immediate: true },
    );

    // --- Color LUT --- //

    interface PaletteData {
      RedPaletteColorLookupTableDescriptor: number[];
      GreenPaletteColorLookupTableDescriptor: number[];
      BluePaletteColorLookupTableDescriptor: number[];
      RedPaletteColorLookupTableData: number[];
      GreenPaletteColorLookupTableData: number[];
      BluePaletteColorLookupTableData: number[];
    }

    const dicomColorLUTData = computed(() => {
      const data = {
        RedPaletteColorLookupTableDescriptor:
          currentStudyStore.imageInfoDicom[curImageID.value]
            ?.RedPaletteColorLookupTableDescriptor ?? [],
        GreenPaletteColorLookupTableDescriptor:
          currentStudyStore.imageInfoDicom[curImageID.value]
            ?.GreenPaletteColorLookupTableDescriptor ?? [],
        BluePaletteColorLookupTableDescriptor:
          currentStudyStore.imageInfoDicom[curImageID.value]
            ?.BluePaletteColorLookupTableDescriptor ?? [],

        RedPaletteColorLookupTableData:
          currentStudyStore.imageInfoDicom[curImageID.value]
            ?.RedPaletteColorLookupTableData,
        GreenPaletteColorLookupTableData:
          currentStudyStore.imageInfoDicom[curImageID.value]
            ?.GreenPaletteColorLookupTableData,
        BluePaletteColorLookupTableData:
          currentStudyStore.imageInfoDicom[curImageID.value]
            ?.BluePaletteColorLookupTableData,
      };
      if (
        data.RedPaletteColorLookupTableData
        && data.GreenPaletteColorLookupTableData
        && data.BluePaletteColorLookupTableData
      ) {
        return data;
      }
      return undefined;
    });

    function buildPaletteColorTransferFunction(palette: PaletteData) {
      const [lenR, firstMappedR, bitsR]
        = palette.RedPaletteColorLookupTableDescriptor;
      const [lenG /* firstMappedG */, , bitsG]
        = palette.GreenPaletteColorLookupTableDescriptor;
      const [lenB /* firstMappedB */, , bitsB]
        = palette.BluePaletteColorLookupTableDescriptor;

      // DICOM quirk: length 0 means 65536 entries
      const entriesR = lenR === 0 ? 65536 : lenR;
      const entriesG = lenG === 0 ? 65536 : lenG;
      const entriesB = lenB === 0 ? 65536 : lenB;

      // Use the minimum entries across components and actual data lengths
      const size = Math.min(
        entriesR,
        entriesG,
        entriesB,
        palette.RedPaletteColorLookupTableData.length,
        palette.GreenPaletteColorLookupTableData.length,
        palette.BluePaletteColorLookupTableData.length,
      );

      // Assume descriptors are consistent across R/G/B
      const firstMapped = firstMappedR;

      const maxR = (1 << bitsR) - 1;
      const maxG = (1 << bitsG) - 1;
      const maxB = (1 << bitsB) - 1;

      // vtkColorTransferFunction.buildFunctionFromTable expects [r,g,b, r,g,b, ...] in [0,1]
      const table = Array.from<number>({ length: size * 3 });

      for (let i = 0; i < size; i++) {
        const r = palette.RedPaletteColorLookupTableData[i] / maxR;
        const g = palette.GreenPaletteColorLookupTableData[i] / maxG;
        const b = palette.BluePaletteColorLookupTableData[i] / maxB;

        const base = i * 3;
        table[base + 0] = r;
        table[base + 1] = g;
        table[base + 2] = b;
      }

      const ctf = vtkColorTransferFunction.newInstance();
      ctf.buildFunctionFromTable(
        firstMapped,
        firstMapped + size - 1,
        size,
        table,
      );

      return ctf;
    }

    const paletteCTF = ref<vtkColorTransferFunction | null>(null);

    const useColorLut = computed(() =>
      viewportStateStore.getUseColorLut(viewID.value),
    );

    watchEffect(() => {
      // ToDo error handling
      if (
        !baseImageRep.value
        || !curImageData.value
        || !wlConfig.value
        || !dicomColorLUTData.value
        || !useColorLut.value
      ) {
        paletteCTF.value = null;
        // Remove palette CTF from actor and re-render
        try {
          const repAny = baseImageRep.value as any;
          const actors = repAny?.getActors?.() ?? [];
          const imageActor = actors.find((a: any) => a.isA?.('vtkImageSlice'));
          if (imageActor) {
            imageActor.getProperty().setRGBTransferFunction(0, null);
          }
          viewProxy.value?.renderLater();
        }
        catch {
          /* ignore if rep not ready */
        }
        return;
      }

      const baseRep = baseImageRep.value;
      const curImage = curImageData.value;
      const wl = wlConfig.value;
      const palette = dicomColorLUTData.value as PaletteData | null;

      if (!baseRep || !curImage || !wl || !palette) return;

      // Build CTF once; re-use on WL changes
      if (!paletteCTF.value) {
        paletteCTF.value = buildPaletteColorTransferFunction(palette);
      }

      const { width, level } = wl;
      const ctf = paletteCTF.value!;

      // For palettes, you usually let the CTF cover the palette index range.
      // Window/level is typically applied via image property below, so you
      // often *don't* need to keep changing setMappingRange here.
      //
      // But if you want WL to shift which palette indices you use:
      //
      // const minWindowVal = level - width / 2;
      // const maxWindowVal = level + width / 2;
      // ctf.setMappingRange(minWindowVal, maxWindowVal);

      try {
        const repAny = baseRep as any;
        const actors = repAny.getActors?.() ?? [];
        if (!actors.length) return;

        const imageActor = actors.find((actor: any) =>
          actor.isA?.('vtkImageSlice'),
        );
        if (!imageActor) return;

        const prop = imageActor.getProperty();
        if (!prop) return;

        // Apply palette as RGB transfer function for component 0
        prop.setRGBTransferFunction(0, ctf);

        // Apply window/level on the slice property
        prop.setColorLevel(level);
        prop.setColorWindow(width);

        viewProxy.value?.renderLater();
      }
      catch (error) {
        console.error('Error applying palette LUT:', error);
      }
    });

    // --- camera setup --- //

    // --- insertion point angle --- //
    // only used for visualization, no user interaction

    // if we want to allowdynamic changes for point changes watcher
    // would be needed, out of scope for now

    // calculate for the first valid angle of slice
    const insertionPointAngle = computed(() => {
      if (!rotateToRefPoint.value) return 0;
      const { seriesIdx, sliceIdx } = currentImageIndices.value;
      const sliceImageIDs = currentStudyStore.getSliceImageIDs(
        seriesIdx,
        sliceIdx,
      );
      const visAngle = (30 * Math.PI) / 180;
      const matchingID = sliceImageIDs.find(
        imageID => calculateInsertionPointAngle(imageID) !== 0,
      );
      if (matchingID === undefined) return 0;
      return calculateInsertionPointAngle(matchingID) + visAngle;
    });

    const { cameraDirVec, cameraUpVec } = useCameraOrientation(
      viewDirection,
      viewUp,
      curImageMetadata,
    );

    function applyInsertionRotation() {
      if (!rotateToRefPoint.value || insertionPointAngle.value <= 0) return;
      const camera = viewProxy.value.getCamera();
      const rotationRad = insertionPointAngle.value;
      const up = [...cameraUpVec.value];
      const dir = [...cameraDirVec.value];
      const cos = Math.cos(rotationRad);
      const sin = Math.sin(rotationRad);
      const dot = up[0] * dir[0] + up[1] * dir[1] + up[2] * dir[2];
      const cross = [
        dir[1] * up[2] - dir[2] * up[1],
        dir[2] * up[0] - dir[0] * up[2],
        dir[0] * up[1] - dir[1] * up[0],
      ];
      const newUp = [
        up[0] * cos + cross[0] * sin + dir[0] * dot * (1 - cos),
        up[1] * cos + cross[1] * sin + dir[1] * dot * (1 - cos),
        up[2] * cos + cross[2] * sin + dir[2] * dot * (1 - cos),
      ];
      camera.setViewUp(newUp[0], newUp[1], newUp[2]);
    }

    // // we store the camera setting based on series and orientation
    const cameraID = computed(() => {
      const o = curImageMetadata.value.orientation;
      const normalKey = `${o[6].toFixed(4)},${o[7].toFixed(4)},${o[8].toFixed(
        4,
      )}`;
      return `${currentSeriesIndexString.value}-${normalKey}`;
    });

    const { resizeToFit, ignoreResizeToFitTracking, resetResizeToFitTracking }
      = useResizeToFit(viewProxy.value.getCamera(), false);

    const resizeToFitScene = () =>
      ignoreResizeToFitTracking(() => {
        // resize to fit
        const lookAxis
          = curImageMetadata.value.lpsOrientation[
            getLPSAxisFromDir(viewDirection.value)
          ];

        const upAxis
          = curImageMetadata.value.lpsOrientation[
            getLPSAxisFromDir(viewUp.value)
          ];
        const dimsWithSpacing = curImageMetadata.value.dimensions.map(
          (d, i) => d * curImageMetadata.value.spacing[i],
        );
        viewProxy.value.resizeToFit(lookAxis, upAxis, dimsWithSpacing);
        resetResizeToFitTracking();
      });

    const resetCamera = () => {
      const bounds = curImageMetadata.value.worldBounds;
      const center = [
        (bounds[0] + bounds[1]) / 2,
        (bounds[2] + bounds[3]) / 2,
        (bounds[4] + bounds[5]) / 2,
      ] as vec3;

      // do not track resizeToFit state
      ignoreResizeToFitTracking(() => {
        viewProxy.value.updateCamera(
          cameraDirVec.value,
          cameraUpVec.value,
          center,
        );
        viewProxy.value.resetCamera(bounds);
      });

      resizeToFitScene();

      // default zoom in
      const camera = viewProxy.value.getCamera();
      const currentScale = camera.getParallelScale();
      camera.setParallelScale(currentScale / 2);

      applyInsertionRotation();
      viewProxy.value.renderLater();
    };

    manageVTKSubscription(
      viewProxy.value.onResize(() => {
        if (resizeToFit.value) {
          resizeToFitScene();
        }
      }),
    );

    // if we re-enable resizeToFit, reset the camera
    watch(resizeToFit, () => {
      if (resizeToFit.value) {
        resetCamera();
        // required initially
        // console.log('Resize to fit');
        viewProxy.value.render();
      }
    });

    const { restoreCameraConfig } = usePersistCameraConfig(
      viewID,
      cameraID,
      viewProxy,
      'parallelScale',
      'position',
      'focalPoint',
      'directionOfProjection',
      'viewUp',
    );

    watch(
      [curImageData, cameraDirVec, cameraUpVec],
      () => {
        if (mainImageOrientation.value === undefined) {
          return;
        }
        if (!curImageMetadata.value) {
          return;
        }
        if (!curImageData.value) {
          return;
        }

        const cameraConfig = viewCameraStore.getConfig(
          viewID.value,
          cameraID.value,
        );
        /*
        console.log('Camera ID', cameraID.value);
        if (!cameraConfig) {
          console.log('Non Existing camera config');
        }
        */
        // If we have a saved camera configuration restore it
        if (cameraConfig) {
          // console.log('Existing camera config', cameraConfig);

          restoreCameraConfig(cameraConfig);

          viewProxy.value
            .getRenderer()
            .resetCameraClippingRange(curImageMetadata.value.worldBounds);
          // console.log('ClippingRange:', viewProxy.value.getRenderer().getActiveCamera().getClippingRange());
          applyInsertionRotation();
          viewProxy.value.renderLater();

          // Prevent resize
          resizeToFit.value = false;
        }
        else if (resizeToFit.value) {
          resetCamera();
        }
        else {
          // this will trigger a resetCamera() call
          resizeToFit.value = true;
        }
      },
      { immediate: true, deep: true },
    );

    const vecClose = (a?: number[], b?: number[]) =>
      !!a
      && !!b
      && a.length === b.length
      && a.every((v, i) => Math.abs(v - b[i]) < 1e-6);
    // for camera config on app state loading
    watch(
      () => viewCameraStore.getConfig(viewID.value, cameraID.value),
      (cam) => {
        if (!cam || !curImageMetadata.value) return;
        const camera = viewProxy.value.getCamera();
        // change came from our own onModified persist → already matches → ignore
        const inSync
          = vecClose(camera.getPosition(), cam.position)
            && vecClose(camera.getFocalPoint(), cam.focalPoint)
            && (cam.parallelScale == null
              || Math.abs(camera.getParallelScale() - cam.parallelScale) < 1e-6);
        if (inSync) return;
        restoreCameraConfig(cam); // already guarded by blockPersistingCameraConfig
        viewProxy.value
          .getRenderer()
          .resetCameraClippingRange(curImageMetadata.value.worldBounds);
        applyInsertionRotation();
        viewProxy.value.renderLater();
      },
      { deep: true },
    );

    // --- viewport orientation/camera labels --- //

    const {
      top: topLabel,
      left: leftLabel,
      bottom: bottomLabel,
      right: rightLabel,
    } = useOrientationLabels(viewProxy);

    // --- apply windowing and slice configs --- //;

    watchEffect(() => {
      const rep = baseImageRep.value;
      if (!rep) return;

      if (wlConfig.value) {
        rep.setWindowWidth(wlConfig.value.width);
        rep.setWindowLevel(wlConfig.value.level);
      }
      if (sliceConfig.value) {
        const { slice } = sliceConfig.value;
        rep.setSlice(slice);
        [...layerReps.value, ...labelmapReps.value].forEach(lRep =>
          lRep.setSlice(slice),
        );
      }
      viewProxy.value.renderLater();
    });

    watch(
      [
        baseImageRep,
        () => viewportStateStore.getImageInterpolationMode(viewID.value),
      ],
      ([rep, mode]) => {
        if (!rep) return;
        const repAny = rep as any;

        if (typeof repAny.setImageInterpolationMode === 'function') {
          repAny.setImageInterpolationMode(mode);
        }
        else {
          // Fallback for reps that don't expose the custom setter.
          const actors = repAny.getActors?.() ?? [];
          const imageActor = actors.find((a: any) => a.isA?.('vtkImageSlice'));
          if (imageActor) {
            const interp = mode === 'nearest' ? 0 : 1;
            imageActor.getProperty().setInterpolationType(interp);
          }
        }

        // Only render if the rep actually has image data connected — avoids
        // triggering the OpenGL mapper 'No input!' warning during startup.
        const ds = repAny.getInputDataSet?.();
        if (ds) {
          viewProxy.value?.renderLater();
        }
      },
      { immediate: true },
    );

    // --- event handling --- //
    const { emitEvent } = useEventBus();

    const previousToolMode = ref<ViewportToolMode>(ViewportToolMode.Pan);
    const sideZoomStartY = ref(0);
    const zooming = ref(false);

    const onSideZoomMove = (currentY: number, canvasHeightZoom: number) => {
      const delta = sideZoomStartY.value - currentY;
      let zoomFactor = 1 + (delta / canvasHeightZoom) * 10;
      const camera = viewProxy.value.getCamera();
      const currentScale = camera.getParallelScale();
      if (zoomFactor < 0) {
        zoomFactor = 1;
      }
      camera.setParallelScale(currentScale * zoomFactor);
      viewProxy.value.render();
    };

    function getMousePositionCanvas(e: MouseEvent) {
      const canvas = e.target as HTMLElement;
      const canvasRect = canvas.getBoundingClientRect();
      const mouseCanvasX = e.clientX - canvasRect.left;
      const mouseCanvasY = e.clientY - canvasRect.top;
      return { mouseCanvasX, mouseCanvasY, canvasRect };
    }

    function handleHoverChange(isHover: boolean) {
      hover.value = isHover;
      if (isHover) {
        viewportStateStore.updateViewportCursor(viewID.value);
        appStateStore.setInteractionViewer(viewID.value);
      }

      if (!isHover) {
        appStateStore.setInteractionViewer(null);
      }
    }

    function handleMouseDown(e: MouseEvent) {
      // done above
      e.preventDefault();
      if (hover.value) {
        if (e.button === 0) {
          if (
            viewportStateStore.getToolMode(viewID.value)
            === ViewportToolMode.ZoomSide
          ) {
            const { mouseCanvasY } = getMousePositionCanvas(e);
            sideZoomStartY.value = mouseCanvasY;
            zooming.value = true;
            return;
          }
        }
        emitEvent('mousedown', e);
      }
    }

    function handleMouseMove(e: MouseEvent) {
      e.preventDefault();
      const { mouseCanvasX, canvasRect } = getMousePositionCanvas(e);
      const currentMode = viewportStateStore.getToolMode(viewID.value);
      const inZoomZone = mouseCanvasX > canvasRect.width - 10;

      if (inZoomZone && currentMode !== ViewportToolMode.ZoomSide) {
        viewProxy.value.getInteractorStyle2D().resetCurrentManipulator();
        previousToolMode.value = currentMode;
        viewportStateStore.setToolMode(viewID.value, ViewportToolMode.ZoomSide);
      }
      else if (
        !inZoomZone
        && currentMode === ViewportToolMode.ZoomSide
        && !zooming.value
      ) {
        viewportStateStore.setToolMode(viewID.value, previousToolMode.value);
      }

      if (zooming.value) {
        const { mouseCanvasY } = getMousePositionCanvas(e);
        onSideZoomMove(mouseCanvasY, canvasRect.height);
        sideZoomStartY.value = mouseCanvasY;
      }
      else if (hover.value) {
        emitEvent('mousemove', e);
      }
    }

    function handleMouseUp(e: MouseEvent) {
      e.preventDefault();
      if (
        viewportStateStore.getToolMode(viewID.value)
        === ViewportToolMode.ZoomSide
      ) {
        sideZoomStartY.value = 0;
        zooming.value = false;
        viewportStateStore.setToolMode(viewID.value, previousToolMode.value);
        previousToolMode.value = ViewportToolMode.Pan;
        // } else if (hover.value) {
        //   emitEvent('mouseup', e);
        // }
      }
      else {
        emitEvent('mouseup', e);
      }
    }

    function handleMouseClick(e: MouseEvent) {
      e.preventDefault();
      if (e.ctrlKey && e.button === 0) return; // Ctrl+click = context menu on Safari, not a click
      if (hover.value) {
        emitEvent('mouseclick', e);
      }
    }

    function handleMouseDoubleClick(e: MouseEvent) {
      e.preventDefault();
      if (hover.value) {
        viewportStateStore.setToolMode(viewID.value, ViewportToolMode.Pan);
        viewportStateStore.setMoveContour(viewID.value, null, '', [0, 0]);
        viewportStateStore.setHideDrawingContour(viewID.value, null, '');
        viewportStateStore.setHideDrawingContourLabel(viewID.value, null);
        viewportStateStore.setSelectedContour(viewID.value, null, null);
        emitEvent('mousedbclick', e);
      }
    }

    function handleWheel(e: MouseEvent) {
      if (hover.value) {
        emitEvent('mousewheel', e);
      }
    }

    // document.addEventListener('contextmenu', (event) => {
    //   event.preventDefault();
    // });

    function handleContextMenu(e: MouseEvent) {
      e.preventDefault();
      if (hover.value) {
        emitEvent('contextmenu', e);
      }
    }

    function handleMouseEnter(e: MouseEvent) {
      if (hover.value) {
        emitEvent('mouseenter', e);
      }
    }

    function handleMouseLeave(e: MouseEvent) {
      if (hover.value) {
        emitEvent('mouseleave', e);
      }
    }

    function handleFocusIn(e: FocusEvent) {
      emitEvent('focusin', e);
    }

    function handleFocusOut(e: FocusEvent) {
      emitEvent('focusout', e);
    }

    const canEditContours = computed(() => {
      return checkComponentViewIDPermission('UIDAdjustContours', viewID.value);
    });

    const toggleContours = () => {
      contoursToggledOff.value = showContours.value;
      viewportStateStore.setShowContours(viewID.value, !showContours.value);
    };

    const toggleColorLut = () => {
      viewportStateStore.setUseColorLut(viewID.value, !useColorLut.value);
      // for now use same button
      viewportStateStore.setShowOverlayImages(
        viewID.value,
        !showOverlayImages.value,
      );
    };

    const toggleAnnotations = () => {
      viewportStateStore.setShowAnnotations(
        viewID.value,
        !showAnnotations.value,
      );
    };

    const TARGET_KEY_TOGGLE_CONTOURS_WINDOWTOOL = 'Shift';

    const onShiftDown = (event: KeyboardEvent) => {
      if (event.repeat) return; // Ignore repeated key presses
      if (!curImageID.value || !hover.value) return;
      viewportStateStore.setShowContours(viewID.value, false);
      viewportStateStore.setShowMasks(viewID.value, false);
      previousToolMode.value = viewportStateStore.getToolMode(viewID.value);
      viewportStateStore.setToolMode(
        viewID.value,
        ViewportToolMode.WindowLevel,
      );
      // event.stopPropagation();
    };

    // const onMetaDown = (event: KeyboardEvent) => {
    //   if (event.repeat) return; // Ignore repeated key presses
    //   if (!curImageID.value || !hover.value) return;
    //   previousToolMode.value = viewportStateStore.getToolMode(viewID.value);
    //   viewportStateStore.setToolMode(viewID.value, ViewportToolMode.Zoom);
    //   // event.stopPropagation();
    // };

    const onButtonUp = (event: KeyboardEvent) => {
      if (event.key === TARGET_KEY_TOGGLE_CONTOURS_WINDOWTOOL) {
        if (!contoursToggledOff.value) {
          viewportStateStore.setShowContours(viewID.value, true);
        }

        if (!masksToggledOff.value) {
          viewportStateStore.setShowMasks(viewID.value, true);
        }

        viewportStateStore.setToolMode(viewID.value, previousToolMode.value);
        previousToolMode.value = ViewportToolMode.Pan;
      }

      // if (event.key === TARGET_KEY_TOGGLE_ZOOM) {
      //   viewportStateStore.setToolMode(viewID.value, previousToolMode.value);
      //   previousToolMode.value = ViewportToolMode.Pan;
      // }
      // // event.stopPropagation
    };

    onKeyStroke(TARGET_KEY_TOGGLE_CONTOURS_WINDOWTOOL, onShiftDown);
    // onKeyStroke(TARGET_KEY_TOGGLE_ZOOM, onMetaDown);
    window.addEventListener('keyup', onButtonUp);

    return {
      // Cine Playing:
      playCine,
      cineFramerate,
      cineRunning,
      isCineSeries,
      reduceFramerate,
      increaseFramerate,
      viewDirection,
      viewUp,
      curImageID,
      onDrop,
      debugProxyView,
      captureScreenshot,
      captureMovie,
      movieRecording,
      screenshotDialog,
      screenshotDataUrl,
      handleScreenshotSave,
      handleScreenshotDiscard,
      currentImageIndicesValid,

      showContours,
      canEditContours,
      useColorLut,
      showMasks,
      showOverlayImages,
      toggleColorLut,
      toggleMasks,
      clearViewport,
      handleHoverChange,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleMouseClick,
      handleMouseDoubleClick,
      handleMouseLeave,
      handleMouseEnter,
      handleFocusIn,
      handleFocusOut,
      handleWheel,
      canvasWidth,
      canvasHeight,
      updateCanvasSize,
      toggleContours,
      toggleAnnotations,
      showAnnotations,

      curSeriesID,
      currentImageIndices,
      numImages,
      formattedContourValues,

      vtkContainerRef,
      annotationOverlayRef,
      contourOverlayRef,
      maskOverlayRef,
      toolContainer,
      viewID,
      viewTitle,
      viewProxy,
      viewAxis,
      active: true,
      sliceMin,
      sliceMax,
      windowWidth,
      windowLevel,
      topLabel,
      bottomLabel,
      leftLabel,
      rightLabel,
      currentVtkImageAvailableForRendering,
      widgetManager,
      dicomInfo,
      enableResizeToFit() {
        resizeToFit.value = true;
      },
      resetWindowLevelToDefault,
      hover,
      handleContextMenu,

      selectedMaskType,
      selectedBiomarker,
      availableBiomarkers,
      hasAnalyzedSelection,
      selectMaskType,
      selectBiomarker,
      getLabelFromBiomarkerType,
      availableBiomarkerOptions,
    };
  },
});
</script>

<template>
  <div
    class="vtk-container-wrapper"
    tabindex="0"
    @pointerenter="handleHoverChange(true)"
    @pointerleave="handleHoverChange(false)"
    @focusin="handleHoverChange(true)"
    @focusout="handleHoverChange(false)"
  >
    <!-- Title bar placed before the container -->
    <div
      class="view-title-bar simple-panel py-0.5 px-1 flex align-center justify-between rounded-t-lg"
    >
      <div class="view-bar-button-container left-section">
        <v-btn
          v-if="isCineSeries && cineRunning"
          v-tooltip:bottom="'Stop Cine'"
          icon
          size="small"
          variant="text"
          @click="playCine"
        >
          <i class="fa-solid fa-circle-stop default-icon" />
        </v-btn>

        <v-btn
          v-if="isCineSeries && !cineRunning"
          v-tooltip:bottom="'Play Cine'"
          icon
          size="small"
          variant="text"
          @click="playCine"
        >
          <i class="fa-solid fa-circle-play default-icon" />
        </v-btn>
        <p class="view-title-text">
          {{ viewTitle }}
        </p>

        <!-- Mask selection controls (only visible for AnalysisLeft and AnalysisRight) -->
        <div
          v-if="
            hasAnalyzedSelection
              && (viewID === 'AnalysisLeft' || viewID === 'AnalysisRight')
          "
          class="mask-controls-container ml-4"
        >
          <div class="mask-toggle-group">
            <button
              class="mask-toggle-btn"
              :class="{ active: selectedMaskType === 'Dynamic' }"
              @click="selectMaskType('Dynamic')"
            >
              Dynamic
            </button>
            <button
              class="mask-toggle-btn"
              :class="{ active: selectedMaskType === 'Static' }"
              @click="selectMaskType('Static')"
            >
              Static
            </button>
          </div>

          <div class="biomarker-dropdown-wrapper">
            <GlassDropdown
              :model-value="selectedBiomarker"
              :options="availableBiomarkerOptions"
              @update:model-value="
                (biomarker) => selectBiomarker(selectedMaskType, biomarker)
              "
            />
          </div>
        </div>
      </div>

      <div class="view-bar-button-container right-section">
        <v-btn
          v-tooltip:bottom="'Toggle Contour Display'"
          icon
          size="small"
          variant="text"
          :class="{ 'toggle-on': showContours, 'toggle-off': !showContours }"
          @click="toggleContours"
        >
          <v-icon size="medium" class="default-icon">
            mdi mdi-gesture
          </v-icon>
        </v-btn>
        <v-btn
          v-tooltip:bottom="'Toggle Overlay'"
          icon
          size="small"
          variant="text"
          :class="{ 'toggle-on': showMasks, 'toggle-off': !showMasks }"
          @click="toggleMasks"
        >
          <v-icon size="medium" class="default-icon">
            mdi mdi-transition-masked
          </v-icon>
        </v-btn>

        <v-btn
          v-tooltip:bottom="'Toggle Color LUT'"
          icon
          size="small"
          variant="text"
          :class="{ 'toggle-on': useColorLut, 'toggle-off': !useColorLut }"
          @click="toggleColorLut"
        >
          <v-icon size="medium" class="default-icon">
            mdi mdi-palette
          </v-icon>
        </v-btn>

        <v-btn
          v-tooltip:bottom="'Toggle Text Annotations'"
          icon
          size="small"
          variant="text"
          :class="{
            'toggle-on': showAnnotations,
            'toggle-off': !showAnnotations,
          }"
          @click="toggleAnnotations"
        >
          <v-icon size="medium" class="default-icon">
            mdi mdi-text-recognition
          </v-icon>
        </v-btn>

        <v-btn
          v-tooltip:bottom="'Clear Viewport'"
          icon
          size="small"
          variant="text"
          @click="clearViewport"
        >
          <v-icon size="medium" class="default-icon">
            mdi mdi-eraser
          </v-icon>
        </v-btn>

        <v-btn
          v-tooltip:bottom="'Reset View'"
          icon
          size="small"
          variant="text"
          @click="
            () => {
              resetWindowLevelToDefault();
              enableResizeToFit();
            }
          "
        >
          <v-icon size="medium" class="default-icon">
            mdi-camera-flip-outline
          </v-icon>
        </v-btn>

        <v-btn
          v-if="isCineSeries"
          v-tooltip:bottom="'Save Movie (MP4)'"
          icon
          size="small"
          variant="text"
          :loading="movieRecording"
          :disabled="movieRecording"
          @click="captureMovie()"
        >
          <v-icon size="medium" class="default-icon">
            mdi mdi-video
          </v-icon>
        </v-btn>

        <v-btn
          v-tooltip:bottom="'Capture Screenshot'"
          icon
          size="small"
          variant="text"
          @click="captureScreenshot"
        >
          <v-icon size="medium" class="default-icon">
            mdi mdi-camera
          </v-icon>
        </v-btn>
      </div>
    </div>

    <div
      class="vtk-container"
      :class="active ? 'active' : ''"
      @click.prevent="handleMouseClick"
      @dblclick.prevent="handleMouseDoubleClick"
      @mousedown.prevent="handleMouseDown"
      @mousemove.prevent="handleMouseMove"
      @mouseup.prevent="handleMouseUp"
      @mouseleave="handleMouseLeave"
      @focusout="handleFocusOut"
      @mouseenter="handleMouseEnter"
      @focusin="handleFocusIn"
      @wheel.prevent="handleWheel"
      @drop="onDrop($event)"
      @dragover.prevent
      @dragenter.prevent
      @contextmenu.prevent="handleContextMenu"
    >
      <div class="vtk-sub-container">
        <div ref="vtkContainerRef" class="vtk-view" />
      </div>

      <UIDDisplayGrid
        :id="viewID"
        :current-image-uid="curImageID"
        :canvas-width="canvasWidth"
        :canvas-height="canvasHeight"
      />

      <UIDDisplayContours
        v-show="!disableContours && showContours"
        :id="viewID"
        ref="contourOverlayRef"
        :active="hover"
        :current-image-uid="curImageID"
        :show-contours="showContours"
        :canvas-width="canvasWidth"
        :canvas-height="canvasHeight"
        @update-contour-format="formattedContourValues = $event"
      />

      <UIDAdjustContours
        v-if="!disableContours && canEditContours"
        :id="viewID"
        :active="hover"
        :current-image-uid="curImageID"
        :show-contours="showContours"
        :canvas-width="canvasWidth"
        :canvas-height="canvasHeight"
        @force-hover="handleHoverChange"
      />

      <UIDDisplayMasks
        v-show="showMasks"
        :id="viewID"
        ref="maskOverlayRef"
        :cur-image-i-d="curImageID"
        :show-masks="showMasks"
        :canvas-width="canvasWidth"
        :canvas-height="canvasHeight"
      />

      <div ref="toolContainer" class="overlay-no-events tool-layer">
        <PanTool :view-id="viewID" />
        <ZoomTool :view-id="viewID" />
        <RotateTool :view-id="viewID" />
        <WindowLevelTool :view-id="viewID" />
      </div>
      <ViewOverlayGrid
        ref="annotationOverlayRef"
        class="overlay-no-events view-annotations py-1 px-2"
        :class="{ hidden: !showAnnotations }"
      >
        <template #top-left>
          <div class="annotation-cell w-min">
            <!--             <h5 class="text-on-viewer">
              {{ dicomInfo.institutionName }}
            </h5> -->
            <h5 class="text-on-viewer">
              Image Idx: {{ currentImageIndices.seriesIdx + 1 }}/{{
                currentImageIndices.sliceIdx + 1
              }}/{{ currentImageIndices.imageIdx + 1 }}
            </h5>
            <h5 class="text-on-viewer">
              HR: {{ dicomInfo.imageCalculatedHeartRate.toFixed(0) }}/min
            </h5>
            <br>
            <h5 class="text-on-viewer">
              {{ dicomInfo.imageVencImageDescription }}
            </h5>
          </div>
        </template>
        <template #middle-left>
          <div class="annotation-cell pointer-events-all mt-4">
            <div v-if="isCineSeries && cineRunning" class="cine-speed-control">
              <v-icon
                size="x-large"
                color="var(--md-text-on-viewer)"
                style="height: 15px; cursor: pointer"
                @mousedown.stop="increaseFramerate"
              >
                mdi mdi-menu-up
              </v-icon>
              <h5 class="text-on-viewer">
                FPS: {{ Math.floor(1000 / cineFramerate) }}
              </h5>
              <v-icon
                size="x-large"
                style="height: 20px; cursor: pointer"
                color="var(--md-text-on-viewer)"
                @mousedown.stop="reduceFramerate"
              >
                mdi mdi-menu-down
              </v-icon>
            </div>
            <div v-else class="w-min">
              <h6 class="text-on-viewer">
                {{ leftLabel }}
              </h6>
            </div>
          </div>
        </template>
        <template #top-center>
          <div class="annotation-cell w-min mx-auto">
            <h6 class="text-on-viewer">
              {{ topLabel }}
            </h6>
          </div>
        </template>
        <template #middle-right>
          <div class="annotation-cell w-min ml-auto">
            <h6 class="text-on-viewer">
              {{ rightLabel }}
            </h6>
          </div>
        </template>
        <template #bottom-left>
          <div class="annotation-cell w-min">
            <h5 class="text-on-viewer">
              Dims: {{ dicomInfo.imageColumns }}x{{ dicomInfo.imageRows }}
            </h5>
            <h5 class="text-on-viewer">
              STh: {{ dicomInfo.imageSliceThickness.toFixed(0) }}mm SL:
              {{ dicomInfo.imageCalculatedSliceLocation.toFixed(2) }}
            </h5>
            <h5 class="text-on-viewer">
              TD: {{ dicomInfo.imageTriggerTime.toFixed(0) }}ms ST:
              {{ dicomInfo.imageCalculatedSliceTime.toFixed(0) }}ms
            </h5>
            <!--             <h5 class="text-on-viewer">
              ImageComments: {{ dicomInfo.imageImageComments }}
            </h5> -->
            <h5
              v-if="
                typeof windowWidth === 'number'
                  && typeof windowLevel === 'number'
              "
              class="text-on-viewer"
            >
              W/L: {{ windowWidth.toFixed(0) }}/{{ windowLevel.toFixed(0) }}
            </h5>
            <h5 class="text-on-viewer">
              {{ dicomInfo.seriesDescription }} {{ dicomInfo.seriesNumber }}/{{
                dicomInfo.imageInstanceNumber
              }}
            </h5>
          </div>
        </template>

        <template #bottom-center>
          <div
            v-if="formattedContourValues"
            class="annotation-cell px-4 py-1 w-min justify-self-center"
          >
            <pre class="text-on-viewer" style="font-size: 0.65rem">{{
              formattedContourValues
            }}</pre>
          </div>
          <div v-else class="annotation-cell w-min mx-auto">
            <h6 class="text-on-viewer">
              {{ bottomLabel }}
            </h6>
          </div>
        </template>
      </ViewOverlayGrid>
      <CornerMenu
        v-if="currentImageIndicesValid"
        :radius="120"
        mode="tool"
        :viewport-id="viewID"
        position="top-right"
        style="position: absolute; top: 0; right: 0; z-index: 1000"
      />
      <CornerMenu
        v-if="currentImageIndicesValid && !disableContours"
        :radius="120"
        mode="drawing"
        :viewport-id="viewID"
        position="bottom-right"
        style="position: absolute; bottom: 0; right: 0; z-index: 1000"
      />
      <transition name="loading">
        <div
          v-if="!currentVtkImageAvailableForRendering"
          class="overlay-no-events loading-image"
        >
          <div>
            <v-progress-circular indeterminate color="var(--md-primary)" />
          </div>
        </div>
      </transition>
      <transition name="noimage">
        <div
          v-if="!currentImageIndicesValid"
          class="image-placeholder flex flex-col gap-2"
          style="
            position: absolute;
            width: 100%;
            height: 100%;
            pointer-events: auto;
            user-select: none;
            cursor: default;
          "
          @click.stop.prevent
          @mousedown.stop.prevent
          @mouseup.stop.prevent
          @mousemove.stop.prevent
          @wheel.stop.prevent
          @contextmenu.stop.prevent
          @keydown.stop.prevent
          @keyup.stop.prevent
        >
          <AppLogoComponent />
          <h2 style="position: absolute; bottom: 5rem; font-style: italic">
            {{ viewTitle }}
          </h2>
        </div>
      </transition>
    </div>

    <!-- Screenshot Dialog -->
    <ScreenshotDialog
      :is-open="screenshotDialog"
      :image-info="{
        imageDataURL: screenshotDataUrl,
        viewerID: viewID,
        type: 'image',
        defaultTitle: viewTitle,
        imageIndices: currentImageIndices,
      }"
      @update:dialog-open="screenshotDialog = $event"
      @save="handleScreenshotSave"
      @discard="handleScreenshotDiscard"
    />
  </div>
</template>

<style scoped src="@/src/components/styles/vtk-view.css"></style>

<style scoped src="@/src/components/styles/utils.css"></style>

<style scoped>
.loading-image {
  display: flex;
  flex-flow: column;
  row-gap: 16px;
  align-items: center;
  justify-content: center;
}

.cine-speed-control {
  height: 25px;
  width: 25px;
  margin-left: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.vtk-container-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  z-index: 0;
  container-type: inline-size;
}

.view-title-bar {
  border-radius: 3px;
  min-width: 0;
  overflow: hidden;
}

.view-bar-button-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 0;
}

.view-bar-button-container.left-section {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
}

.view-bar-button-container.right-section {
  flex: 0 0 auto;
  margin-left: auto;
}

.view-title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  margin: 0;
}

.view-bar-button-container .v-btn {
  min-width: 22px !important;
  width: 22px !important;
  height: 22px !important;
  padding: 0 !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: transparent !important;
}

.view-bar-button-container .v-icon {
  font-size: 16px !important;
}

.vtk-container {
  grid-column: 1;
  grid-row: 2;
  width: 100%;
}

.hidden {
  display: none !important;
}

.toggle-on {
  opacity: 1;
  /* border-radius: 50% !important; */
  width: 20px !important;
  height: 20px !important;
}

.toggle-off {
  opacity: 0.4;
  filter: grayscale(1);
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
  transform: scale(0.95);
}

.annotation-cell .text-on-viewer {
  font-size: 0.65rem;
}

.mask-controls-container {
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;
  min-width: 0;
}

.mask-toggle-group {
  display: flex;
  background: var(--glass-bg-secondary);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  padding: 2px;
  box-shadow: inset 0 1px 3px var(--glass-shadow);
  flex-shrink: 0;
}

.mask-toggle-btn {
  background: transparent;
  border: none;
  color: var(--md-text-secondary);
  padding: 1px 12px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 18px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  text-transform: capitalize;
}

.mask-toggle-btn:hover {
  color: var(--md-text-primary);
  background: var(--glass-bg-tertiary);
}

.mask-toggle-btn.active {
  background: var(--md-primary);
  color: var(--md-on-primary);
  box-shadow: 0 2px 6px var(--glass-shadow);
  font-weight: 600;
}

@container (max-width: 500px) {
  .view-bar-button-container {
    gap: 4px;
  }

  .mask-controls-container {
    gap: 4px;
    margin-left: 4px !important;
  }

  .mask-toggle-btn {
    padding: 1px 6px;
    font-size: 10px;
  }

  .view-title-text {
    display: none !important;
  }

  .view-bar-button-container {
    gap: 2px;
  }
}
</style>
